import history from '../../history';
import { createAuth0Client } from '@auth0/auth0-spa-js'

import IdTokenVerifier from 'idtoken-verifier';

/**
 * Local development auth bypass: skips Auth0 and signs in as a seeded user.
 * Only active under `craco start` on localhost; production builds eliminate it.
 */
export const LOCAL_AUTH_BYPASS =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_LOCAL_AUTH_BYPASS === 'true' &&
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Seeded local users; ids match packages/backend/scripts/dynamodb_console_scripts/node_seed_local.ts.
export const LOCAL_USERS = {
  'admin@edyn.test':  { type: 'admin',  id: '351a5f10-5018-11eb-8174-0b1ef175129a' },
  'carer@edyn.test':  { type: 'carer',  id: '2e79ce10-9fbd-11e8-91f5-4feac24802b9' },
  'carer2@edyn.test': { type: 'carer',  id: '0ad46a00-dec4-11e9-943b-95100f6ca4f6' },
  'client@edyn.test': { type: 'client', id: '186e4b40-33b5-11ea-9bbe-b3dcdc47333c' },
};

const LOCAL_PORTAL = {
  admin: '/portal-admin/visits',
  carer: '/portal-carer/overview',
  client: '/portal-client/dashboard',
};

// Shared dev password for any seeded user; override with REACT_APP_LOCAL_PASSWORD.
export const LOCAL_PASSWORD = process.env.REACT_APP_LOCAL_PASSWORD || 'localdev';

// Feature toggles granted to every local user (there is no Auth0 claim locally).
export const LOCAL_FEATURE_TOGGLES = [
  'CARE_PLAN_V2',
  'EMAR',
  'CARER_TODAY',
  'EMAR_PLATFORM_AUTHORING',
];

// Optional localStorage override of LOCAL_FEATURE_TOGGLES, set via window.edynFlags in the console.
// Kept separate from `edyn-featureToggles`, which the login writes and production reads.
const OVERRIDE_KEY = 'edyn-featureToggles-override'

export const localToggles = () => {
  try {
    const override = localStorage.getItem(OVERRIDE_KEY)

    return override ? JSON.parse(override) : LOCAL_FEATURE_TOGGLES
  } catch (e) {
    console.error('Ignoring an unreadable feature toggle override', e)

    return LOCAL_FEATURE_TOGGLES
  }
}

if (LOCAL_AUTH_BYPASS && typeof window !== 'undefined') {
  const write = (flags) => {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify([...new Set(flags)]))
    console.info('Feature toggles now:', localToggles(), '- reload to apply')
  }

  window.edynFlags = {
    list: () => localToggles(),
    available: () => LOCAL_FEATURE_TOGGLES,
    on: (flag) => write([...localToggles(), flag]),
    off: (flag) => write(localToggles().filter((f) => f !== flag)),
    reset: () => {
      localStorage.removeItem(OVERRIDE_KEY)
      console.info('Override cleared. Back to the declared list:', LOCAL_FEATURE_TOGGLES)
    },
  }
}

const localBypassToken = ({ type, id, email }) => {
  const b64 = (o) =>
    btoa(JSON.stringify(o)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  const claims = {
    // serverless-offline needs `sub` to derive principalId.
    sub: `local|${id}`,
    'http://localhost:3000/user_metadata': {},
    'http://localhost:3000/app_metadata': { type, id },
    email,
  };
  return `${b64({ alg: 'none', typ: 'JWT' })}.${b64(claims)}.`;
};

const auth0Manager = (() => {
  let auth0Client;
  let isAuth0Loading;


  const init = async function() {
    if (!auth0Client && !isAuth0Loading) {
      isAuth0Loading = true
      auth0Client = await createAuth0Client({
        domain: process.env.REACT_APP_AUTH0_DOMAIN,
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
        useRefreshTokens: true,
        cacheLocation:'localstorage',
        authorizationParams: {
          redirect_uri: process.env.REACT_APP_AUTH0_CALLBACK_URL,
          connection: 'Username-Password-Authentication',
          audience: process.env.REACT_APP_API_AUDIENCE,
        }
      })
      isAuth0Loading = false
      return
    }
    throw new Error('Auth0 already initialised')
  }

  const getClient = function() {
    if (!auth0Client) {
      throw new Error('Auth0Client not initialised')
    }

    return auth0Client;
  }

  return {
    init,
    getClient,
  }
})()


export default class Auth {
  verifier = new IdTokenVerifier({
    issuer: process.env.REACT_APP_AUTH0_DOMAIN,
    audience: process.env.REACT_APP_AUTH0_CLIENT_ID
  });

  constructor() {
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getProfile = this.getProfile.bind(this);
    this.userHasAccess = this.userHasAccess.bind(this);
    this.getUserId = this.getUserId.bind(this);
    this.handleSessionRedirect = this.handleSessionRedirect.bind(this);
  }

  async init() {
    if (LOCAL_AUTH_BYPASS) {
      return
    }
    try {
      await auth0Manager.init()
    } catch (error) {
      console.error(error)
    }
  }

  // Seed the localStorage session for a seeded local user without contacting Auth0.
  seedLocalSession(email) {
    const user = LOCAL_USERS[email]
    if (!user) {
      throw new Error(`Unknown local user: ${email}`)
    }
    const token = localBypassToken({ type: user.type, id: user.id, email })
    localStorage.setItem('access_token', token)
    localStorage.setItem('id_token', token)
    localStorage.setItem('expires_at', JSON.stringify(Date.now() + 24 * 60 * 60 * 1000))
    localStorage.setItem('email', email)
    localStorage.setItem('userType', user.type)
    localStorage.setItem('userId', user.id)
    localStorage.setItem('edyn-featureToggles', JSON.stringify(localToggles()))
    localStorage.setItem('signup_status', 'complete')
  }

  // Local email + password sign-in; returns null on success or an error message to display.
  localCredentialLogin(email, password) {
    const key = (email || '').trim().toLowerCase()
    const user = LOCAL_USERS[key]
    if (!user) {
      return 'Unknown user. Use one of the seeded local emails (see the README).'
    }
    if (password !== LOCAL_PASSWORD) {
      return 'Incorrect password.'
    }
    this.seedLocalSession(key)
    window.location.assign(LOCAL_PORTAL[user.type])
    return null
  }

  async login() {
    if (LOCAL_AUTH_BYPASS) {
      history.replace('/login')
      return
    }
    try {
      if (window.location.pathname === '/login-redirect') {
        await auth0Manager.getClient().loginWithRedirect()
        return
      }

      await auth0Manager.getClient().loginWithRedirect({
        appState:window.location
      })
    } catch (error) {
      console.error(error)
    }
  }

  async handleAuthentication() {
    if (LOCAL_AUTH_BYPASS) {
      history.replace('/login')
      return
    }
    try {
      const auth0 = auth0Manager.getClient()
      let getAuth0CallbackState = await auth0.handleRedirectCallback()

      const getTokenResult = await auth0.getTokenSilently({detailedResponse:true});
      const userInfo = await auth0.getUser()
      this.updateLocalStorage(getTokenResult, userInfo)
      this.handleSessionRedirect(userInfo, getAuth0CallbackState?.appState)
    } catch (error) {
      console.error(error)
    }
  }

  async checkIfValidSessionExists() {
    if (LOCAL_AUTH_BYPASS) {
      return this.isAuthenticated()
    }
    const auth0 = auth0Manager.getClient();
    try {
      // 'off': the route guard must ask Auth0 rather than trust a cached token.
      await this.getAccessTokenAsync('off')
      return true
    } catch (error) {
      return false
    }
  }

  updateLocalStorage(authResult, user) {
    let expiresAt = JSON.stringify((authResult.expires_in * 1000) + new Date().getTime());

    localStorage.setItem('access_token', authResult.access_token);
    localStorage.setItem('id_token', authResult.id_token);
    localStorage.setItem('expires_at', expiresAt);
    localStorage.setItem('email', user.email);

    // user_metadata and app_metadata claims are set by an Auth0 post-login action.
    // type/id live in app_metadata (not user-writable), see packages/backend/lib/auth.ts.
    const customClaims = user['http://localhost:3000/user_metadata']
    const appClaims = user['http://localhost:3000/app_metadata'] || {}
    localStorage.setItem('userType', appClaims.type);
    localStorage.setItem('userId', appClaims.id);
    localStorage.setItem('edyn-featureToggles', JSON.stringify(customClaims.featureToggles || []))
    localStorage.setItem('signup_status', customClaims['signup_status'])
  }

  handleSessionRedirect(user, appState) {
      const loginUserType = localStorage.getItem('userType');
      const signupStatus = localStorage.getItem('signup_status')
      if (loginUserType === 'carer') {
        if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
          history.replace(appState.pathname)
          return
        }
        history.replace('/portal-carer/overview');

        return;
      }
      if (loginUserType === 'client') {
        if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
          history.replace(appState.pathname)
          return
        }
        history.replace('/portal-client/dashboard');

        return ;
      }
      if (loginUserType === 'admin') {
        if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
          history.replace(appState.pathname)
          return
        }
        history.replace('/portal-admin/visits');
        return ;
      }

    if (loginUserType === 'lead') {
      if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
        history.replace(appState.pathname)
        return
      }
      history.replace('/portal-client/dashboard');
      return ;
    }

      if (loginUserType === 'incomplete' && signupStatus === 'incomplete') {
        history.replace('/find-my-carer/finish-sign-up');
        return ;
      }

      console.error(`Unkown user type on token ${JSON.stringify(user)}, extracted user type: ${loginUserType}`)

      history.replace('/');
  }

  logout() {
    localStorage.clear();

    if (window.Intercom) {
      Intercom('shutdown')
    }

    if (LOCAL_AUTH_BYPASS) {
      history.replace('/login')
      return
    }

    const auth0 = auth0Manager.getClient()

    auth0.logout({
      clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
      logoutParams:{
        returnTo: process.env.REACT_APP_MARKETING_SITE_URL,
      }
    });
  }

  signup(opts) {
    if (LOCAL_AUTH_BYPASS) {
      history.replace('/login')
      return Promise.resolve()
    }
    const auth0 = auth0Manager.getClient()

    return auth0.loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        login_hint: opts?.email
      }
    })
  }

  getAccessToken() {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }
    return accessToken;
  }

  getProfile(cb) {
    let accessToken = this.getAccessToken();
    this.auth0.client.userInfo(accessToken, (err, profile) => {
      if (profile) {
        this.userProfile = profile;
      }
      cb(err, profile);
    });
  }

  // Not gated on isAuthenticated: expires_at is the access token's, which is refreshed only after callers build their request body.
  getUserId() {
    return localStorage.getItem('userId') ?? undefined;
  }

  getUserName() {
    if (this.isAuthenticated()) {
      return localStorage.getItem('name');
    }
  }
  
  getEmail() {
    if (this.isAuthenticated()) {
      return localStorage.getItem('email');
    }
  }
  
  isAuthenticated() {
    let expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  }

  isFeatureEnabled(featureFlag) {
    if (!this.isAuthenticated()) return false

    // Locally the declared toggles are authoritative, not the cached localStorage copy.
    if (LOCAL_AUTH_BYPASS) return localToggles().includes(featureFlag)

    try {
      return JSON.parse(localStorage.getItem('edyn-featureToggles') || '[]').includes(featureFlag)
    } catch (e) {
      console.error(e)

      return false;
    }
  }

  userHasAccess(userType) {
    const userAccess = localStorage.getItem('userType');
    if (userAccess) {
      return userType.every((type) => userAccess.includes(type));
    }
    return false;
  }

  // Honour cacheMode: forcing 'off' on every call races rotating refresh tokens and Auth0 revokes the family.
  // Callers wanting a genuine server-side check pass 'off' explicitly.
  getAccessTokenAsync(cacheMode) {
    if (LOCAL_AUTH_BYPASS) {
      return Promise.resolve(this.getAccessToken())
    }
    const auth0 = auth0Manager.getClient()
    return auth0.getTokenSilently({detailedResponse: true, ...(cacheMode ? {cacheMode} : {})}).then(async (res) => {
      if (res.access_token !== this.getAccessToken()) {
        const user = await auth0Manager.getClient().getUser()
        this.updateLocalStorage(res, user)  
      }
      return res.access_token
    })
  }
}
